<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== PAGE HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <span class="rm-badge-section">الرحلات</span>
        <h1 class="rm-main-title">إدارة الرحلات</h1>
        <p class="rm-subtitle">نظرة شاملة على مسارات التشغيل وعدد الرحلات المجدولة لكل مسار ومواعيد الانطلاق</p>
      </div>
      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="exportTripsOverviewCsv"
          :disabled="routes.length === 0"
        >
          <v-icon right size="18" class="ms-1">mdi-download</v-icon>
          تصدير CSV
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          @click="goToCreateTrip"
        >
          <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
          إضافة رحلة جديدة
        </v-btn>
      </div>
    </div>

    <!-- ==================== SUMMARY CARDS (3 Cards) ==================== -->
    <div class="rm-cards-grid rm-grid-3 mb-6">
      <!-- Card 1: إجمالي المسارات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">إجمالي المسارات</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value">{{ summary.total_routes }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">إجمالي مسارات الحافلات المسجلة بالنظام</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="26" color="#082C69">mdi-routes</v-icon>
        </div>
      </div>

      <!-- Card 2: المسارات التي بها رحلات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسارات التي بها رحلات</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value rm-val-active">{{ summary.routes_with_trips }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">مسارات مفعلة ومربوطة برحلات مجدولة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="26" color="#10B981">mdi-check-circle-outline</v-icon>
        </div>
      </div>

      <!-- Card 3: إجمالي الرحلات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">إجمالي الرحلات</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value rm-val-amber">{{ summary.total_trips }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">إجمالي الرحلات الرئيسية عبر كافة المسارات</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="26" color="#F59E0B">mdi-bus-clock</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== FILTER & SEARCH BAR ==================== -->
    <div class="rm-filter-card mb-6">
      <div class="rm-filter-row">
        <!-- Search Input -->
        <div class="rm-search-box flex-grow-1">
          <v-text-field
            v-model="searchQuery"
            dense
            outlined
            hide-details
            clearable
            placeholder="البحث باسم المسار أو كود المسار (مثال: R-0021، المعادي)..."
            prepend-inner-icon="mdi-magnify"
            class="rm-input-field"
            @input="onSearchInput"
          ></v-text-field>
        </div>

        <!-- Filter Chips -->
        <div class="rm-filter-chips d-flex align-center gap-2">
          <button
            type="button"
            class="rm-chip-btn"
            :class="{ active: activeFilter === 'all' }"
            @click="setFilter('all')"
          >
            الكل
            <span class="rm-chip-badge">{{ routes.length }}</span>
          </button>
          <button
            type="button"
            class="rm-chip-btn"
            :class="{ active: activeFilter === 'with_trips' }"
            @click="setFilter('with_trips')"
          >
            بها رحلات
            <span class="rm-chip-badge">{{ summary.routes_with_trips }}</span>
          </button>
          <button
            type="button"
            class="rm-chip-btn"
            :class="{ active: activeFilter === 'without_trips' }"
            @click="setFilter('without_trips')"
          >
            بدون رحلات
            <span class="rm-chip-badge">{{ Math.max(0, summary.total_routes - summary.routes_with_trips) }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== ROUTES OVERVIEW TABLE ==================== -->
    <div class="rm-table-card">
      <div class="rm-table-header d-flex align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <div class="rm-icon-header-box me-3">
            <v-icon size="20" color="#082C69">mdi-format-list-bulleted</v-icon>
          </div>
          <div>
            <h2 class="rm-card-title">قائمة المسارات وجدولة الرحلات</h2>
            <p class="rm-card-subtitle">كل صف يمثل مساراً كاملاً وعدد الرحلات التابعة له</p>
          </div>
        </div>
        <span class="rm-table-count">عرض {{ filteredRoutes.length }} من إجمالي {{ routes.length }} مسار</span>
      </div>

      <div class="rm-table-wrapper">
        <table class="rm-modern-table">
          <thead>
            <tr>
              <th class="rm-th-code">كود المسار</th>
              <th class="rm-th-name">اسم المسار</th>
              <th class="rm-th-stations">المحطات (البداية ← النهاية)</th>
              <th class="rm-th-count text-center">عدد الرحلات</th>
              <th class="rm-th-first text-center">أول رحلة</th>
              <th class="rm-th-last text-center">آخر رحلة</th>
              <th class="rm-th-days">أيام العمل</th>
              <th class="rm-th-actions text-center">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="route in paginatedRoutes"
              :key="route.id"
              class="rm-table-row"
            >
              <!-- 1. Route Code -->
              <td class="rm-td-code">
                <span class="rm-code-badge">{{ route.route_code || ('R-' + String(route.id).padStart(4, '0')) }}</span>
              </td>

              <!-- 2. Route Name -->
              <td class="rm-td-name">
                <span class="font-weight-bold text-navy">{{ route.name }}</span>
                <span
                  v-if="route.is_active === false"
                  class="ms-2"
                  style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:700;background:#FEF2F2;color:#DC2626;border:1px solid #FECACA;white-space:nowrap;"
                >غير نشط</span>
              </td>

              <!-- 3. Start & End Stations -->
              <td class="rm-td-stations">
                <div class="d-flex align-center flex-wrap gap-1">
                  <span class="rm-station-name">{{ route.start_stop || '—' }}</span>
                  <v-icon x-small color="#94A3B8" class="mx-1">mdi-arrow-left</v-icon>
                  <span class="rm-station-name">{{ route.end_stop || '—' }}</span>
                  <span class="rm-stops-pill ms-2">{{ route.stops_count }} محطات</span>
                </div>
              </td>

              <!-- 4. Trips Count (Parent trips) -->
              <td class="rm-td-count text-center">
                <span
                  class="rm-trips-badge"
                  :class="route.trips_count > 0 ? 'badge-has-trips' : 'badge-no-trips'"
                >
                  <v-icon x-small class="me-1" :color="route.trips_count > 0 ? '#10B981' : '#94A3B8'">mdi-bus</v-icon>
                  {{ route.trips_count }} {{ route.trips_count === 1 ? 'رحلة' : (route.trips_count === 2 ? 'رحلتان' : 'رحلات') }}
                </span>
              </td>

              <!-- 5. First Trip Time -->
              <td class="rm-td-first text-center font-weight-medium">
                <span v-if="route.first_trip_time" class="rm-time-tag">
                  <v-icon x-small color="#082C69" class="me-1">mdi-clock-outline</v-icon>
                  {{ formatTime(route.first_trip_time) }}
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 6. Last Trip Time -->
              <td class="rm-td-last text-center font-weight-medium">
                <span v-if="route.last_trip_time" class="rm-time-tag">
                  <v-icon x-small color="#082C69" class="me-1">mdi-clock-outline</v-icon>
                  {{ formatTime(route.last_trip_time) }}
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 7. Operating Days -->
              <td class="rm-td-days">
                <span v-if="route.operating_days" class="rm-days-tag">
                  {{ route.operating_days }}
                </span>
                <span v-else class="text--disabled">—</span>
              </td>

              <!-- 8. Actions -->
              <td class="rm-td-actions text-center">
                <v-btn
                  v-if="route.trips_count > 0"
                  depressed
                  small
                  class="rm-btn-view-trips"
                  @click="goToRouteTrips(route.id)"
                >
                  <span>عرض الرحلات</span>
                  <v-icon small class="ms-1">mdi-chevron-left</v-icon>
                </v-btn>

                <v-btn
                  v-else
                  depressed
                  small
                  class="rm-btn-add-trips"
                  @click="goToCreateTripForRoute(route.id)"
                >
                  <v-icon x-small class="me-1">mdi-plus</v-icon>
                  <span>إضافة رحلات</span>
                </v-btn>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="!isLoading && filteredRoutes.length === 0">
              <td colspan="8" class="text-center py-10">
                <div class="rm-empty-state">
                  <v-icon size="48" color="#CBD5E1" class="mb-2">mdi-bus-stop</v-icon>
                  <h3 class="text-h6 font-weight-bold text--secondary mb-1">لا توجد مسارات مطابقة</h3>
                  <p class="text-body-2 text--disabled mb-0">جرّب تغيير عبارة البحث أو الفلتر المختار.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="rm-table-footer d-flex align-center justify-space-between pt-4 mt-2">
        <span class="text-caption text--secondary">
          عرض الصفحة {{ currentPage }} من {{ totalPages }}
        </span>
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          circle
          color="#082C69"
        ></v-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import { exportToCsv } from "@/utils/csvExport";

export default {
  name: "RouteMakingTrips",
  components: {
    VueElementLoading,
  },
  data() {
    return {
      isLoading: false,
      searchQuery: "",
      activeFilter: "all", // 'all', 'with_trips', 'without_trips'
      routes: [],
      summary: {
        total_routes: 0,
        routes_with_trips: 0,
        total_trips: 0,
      },
      currentPage: 1,
      itemsPerPage: 10,
      searchTimeout: null,
    };
  },
  computed: {
    filteredRoutes() {
      let result = this.routes;

      // Filter by trip presence
      if (this.activeFilter === "with_trips") {
        result = result.filter((r) => r.trips_count > 0);
      } else if (this.activeFilter === "without_trips") {
        result = result.filter((r) => r.trips_count === 0);
      }

      // Filter by search query
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.trim().toLowerCase();
        result = result.filter((r) => {
          const name = (r.name || "").toLowerCase();
          const code = (r.route_code || "").toLowerCase();
          const start = (r.start_stop || "").toLowerCase();
          const end = (r.end_stop || "").toLowerCase();
          return name.includes(query) || code.includes(query) || start.includes(query) || end.includes(query);
        });
      }

      return result;
    },
    totalPages() {
      return Math.ceil(this.filteredRoutes.length / this.itemsPerPage) || 1;
    },
    paginatedRoutes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredRoutes.slice(start, start + this.itemsPerPage);
    },
  },
  created() {
    this.fetchRoutesOverview();
  },
  methods: {
    exportTripsOverviewCsv() {
      const routesToExport = this.filteredRoutes && this.filteredRoutes.length > 0 ? this.filteredRoutes : this.routes;
      exportToCsv({
        filename: `نظرة-عامة-الرحلات-${new Date().toISOString().slice(0, 10)}.csv`,
        metadata: [
          { label: "إجمالي المسارات", value: this.summary.total_routes || this.routes.length },
          { label: "المسارات التي بها رحلات", value: this.summary.routes_with_trips || 0 },
          { label: "إجمالي الرحلات المجدولة", value: this.summary.total_trips || 0 },
          { label: "تاريخ التصدير", value: new Date().toLocaleString("ar-EG") },
        ],
        headers: [
          "كود المسار",
          "اسم المسار",
          "محطة البداية",
          "محطة النهاية",
          "عدد الرحلات المجدولة",
          "أول رحلة",
          "آخر رحلة",
          "أيام التشغيل",
        ],
        rows: routesToExport.map((r) => [
          r.route_code || `R-${String(r.id).padStart(4, "0")}`,
          r.name || "",
          r.start_stop || "—",
          r.end_stop || "—",
          r.trips_count || 0,
          this.formatTime(r.first_trip_time),
          this.formatTime(r.last_trip_time),
          r.operating_days || "—",
        ]),
      });
    },
    fetchRoutesOverview() {
      this.isLoading = true;
      axios
        .get("/trips/routes-overview")
        .then((response) => {
          if (response.data) {
            this.routes = response.data.routes || [];
            if (response.data.summary) {
              this.summary = response.data.summary;
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching routes overview:", error);
          this.$toast.error("تعذر تحميل قائمة المسارات والرحلات");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(filter) {
      this.activeFilter = filter;
      this.currentPage = 1;
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
      }, 300);
    },
    formatTime(timeStr) {
      if (!timeStr) return "—";
      const parts = timeStr.split(":");
      if (parts.length < 2) return timeStr;
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "م" : "ص";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    },
    goToRouteTrips(routeId) {
      this.$router.push(`/trips/route/${routeId}`);
    },
    goToCreateTrip() {
      this.$router.push("/trips/create");
    },
    goToCreateTripForRoute(routeId) {
      this.$router.push(`/trips/create?route_id=${routeId}`);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  background: #EEF2F9;
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 10px;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
  display: inline-block;
}

.rm-main-title {
  font-size: 26px;
  font-weight: 800;
  color: #082C69;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 14px;
  color: #64748B;
  margin: 0;
  font-weight: 500;
}

/* Primary Button */
.rm-btn-primary {
  background: #082C69 !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 44px !important;
  border-radius: 10px !important;
  padding: 0 20px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px rgba(8, 44, 105, 0.25) !important;
  transition: all 0.2s ease !important;
}

.rm-btn-primary:hover {
  background: #061B40 !important;
  box-shadow: 0 6px 18px rgba(8, 44, 105, 0.35) !important;
  transform: translateY(-1px);
}

/* Cards Grid */
.rm-cards-grid {
  display: grid;
  gap: 16px;
}

.rm-grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 960px) {
  .rm-grid-3 {
    grid-template-columns: 1fr;
  }
}

.rm-stat-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rm-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.rm-stat-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 4px;
}

.rm-stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #082C69;
  line-height: 1.2;
}

.rm-val-active {
  color: #10B981 !important;
}

.rm-val-amber {
  color: #F59E0B !important;
}

.rm-stat-caption {
  display: block;
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 4px;
}

.rm-stat-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-icon-blue {
  background: #EFF6FF;
}

.rm-icon-green {
  background: #ECFDF5;
}

.rm-icon-amber {
  background: #FFFBEB;
}

/* Filter Card */
.rm-filter-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.rm-chip-btn {
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  color: #475569;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.rm-chip-btn:hover {
  background: #E2E8F0;
}

.rm-chip-btn.active {
  background: #082C69;
  border-color: #082C69;
  color: #FFFFFF;
}

.rm-chip-badge {
  background: rgba(0, 0, 0, 0.08);
  font-size: 0.75rem;
  padding: 1px 6px;
  border-radius: 9999px;
  font-weight: 700;
}

.rm-chip-btn.active .rm-chip-badge {
  background: rgba(255, 255, 255, 0.25);
  color: #FFFFFF;
}

/* Table Card */
.rm-table-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-icon-header-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #EFF6FF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-card-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 0.775rem;
  color: #64748B;
  margin: 0;
}

.rm-table-count {
  font-size: 0.8125rem;
  color: #64748B;
  font-weight: 600;
  background: #F8FAFC;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
}

.rm-table-wrapper {
  overflow-x: auto;
}

.rm-modern-table {
  width: 100%;
  border-collapse: collapse;
}

.rm-modern-table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 12px 14px;
  border-bottom: 2px solid #E2E8F0;
  white-space: nowrap;
}

.rm-modern-table td {
  padding: 14px;
  font-size: 0.85rem;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.rm-table-row:hover {
  background-color: #F8FAFC;
}

/* Badges */
.rm-code-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.825rem;
  background: #EFF6FF;
  color: #082C69;
  border: 1px solid #DBEAFE;
  padding: 3px 8px;
  border-radius: 6px;
}

.text-navy {
  color: #082C69;
}

.rm-station-name {
  color: #1E293B;
  font-weight: 500;
}

.rm-stops-pill {
  font-size: 0.7rem;
  font-weight: 600;
  background: #F1F5F9;
  color: #64748B;
  padding: 2px 7px;
  border-radius: 6px;
}

.rm-trips-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
}

.badge-has-trips {
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}

.badge-no-trips {
  background: #F1F5F9;
  color: #64748B;
  border: 1px solid #E2E8F0;
}

.rm-time-tag {
  display: inline-flex;
  align-items: center;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #082C69;
}

.rm-days-tag {
  display: inline-block;
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #FDE68A;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.775rem;
  font-weight: 600;
}

/* Action Buttons */
.rm-btn-view-trips {
  background: #082C69 !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  font-size: 0.775rem !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
  text-transform: none !important;
}

.rm-btn-add-trips {
  background: #F1F5F9 !important;
  color: #082C69 !important;
  border: 1px solid #CBD5E1 !important;
  font-weight: 700 !important;
  font-size: 0.775rem !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
  text-transform: none !important;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
</style>
